import { supabase } from '@/lib/supabase';

/** Tamaños que se generan automáticamente al subir una imagen. */
export const IMAGE_SIZES = [400, 800, 1200, 1600] as const;

const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // 15 MB
const MIN_DIMENSION = 120;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const WATERMARK_TEXT = 'DK SUPPLY';
const WATERMARK_OPACITY = 0.12;
const WEBP_QUALITY = 0.82;
const STORAGE_BUCKET = 'products';

export interface ImageVariant {
  size: number;
  url: string;
}

export interface OptimizedImage {
  /** URL de la versión principal (pública, optimizada y con marca de agua). */
  url: string;
  /** Prefijo común de las variantes (para construir el srcset). */
  base: string;
  variants: ImageVariant[];
  originalBytes: number;
  optimizedBytes: number;
  width: number;
  height: number;
}

/** Comprueba que el archivo sea una imagen permitida y de tamaño razonable. */
export function validateImageFile(file: File): string | null {
  if (!ALLOWED_MIME.includes(file.type)) {
    return 'Formato no permitido. Usa JPG, PNG o WebP.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return 'La imagen es demasiado grande. Se optimizará automáticamente o debe utilizar una imagen de menor tamaño (máx. 15 MB).';
  }
  return null;
}

/** Comprueba el formato real del archivo por sus primeros bytes (no la extensión). */
async function sniffFormat(file: File): Promise<string | null> {
  const buf = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  if (buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff) return 'image/jpeg';
  if (buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) return 'image/png';
  if (
    buf[0] === 0x52 &&
    buf[1] === 0x49 &&
    buf[2] === 0x46 &&
    buf[3] === 0x46 &&
    buf[8] === 0x57 &&
    buf[9] === 0x45 &&
    buf[10] === 0x42 &&
    buf[11] === 0x50
  ) {
    return 'image/webp';
  }
  return null;
}

async function loadBitmap(file: File): Promise<ImageBitmap> {
  return createImageBitmap(file, { imageOrientation: 'from-image' });
}

/** Dibuja la marca de agua "DK SUPPLY" repetida en mosaico diagonal. */
function drawWatermark(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const fontSize = Math.max(12, Math.round(Math.min(width, height) / 16));
  const diagonal = Math.sqrt(width * width + height * height);
  const stepX = fontSize * 6.5;
  const stepY = fontSize * 4.5;

  ctx.save();
  ctx.globalAlpha = WATERMARK_OPACITY;
  ctx.translate(width / 2, height / 2);
  ctx.rotate(-Math.PI / 6);
  ctx.font = `700 ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = Math.max(1, fontSize / 16);

  let row = 0;
  for (let y = -diagonal; y <= diagonal; y += stepY, row += 1) {
    const offset = row % 2 === 0 ? 0 : stepX / 2;
    for (let x = -diagonal - offset; x <= diagonal; x += stepX) {
      ctx.strokeStyle = 'rgba(0,0,0,0.4)';
      ctx.strokeText(WATERMARK_TEXT, x, y);
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.fillText(WATERMARK_TEXT, x, y);
    }
  }
  ctx.restore();
}

/** Genera una variante redimensionada, comprimida a WebP y con marca de agua. */
async function renderVariant(
  bitmap: ImageBitmap,
  targetWidth: number,
  sourceWidth: number,
  sourceHeight: number
): Promise<Blob> {
  const ratio = sourceHeight / sourceWidth;
  const width = Math.max(1, Math.min(targetWidth, sourceWidth));
  const height = Math.max(1, Math.round(width * ratio));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo procesar la imagen');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);
  drawWatermark(ctx, width, height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', WEBP_QUALITY)
  );
  if (!blob) throw new Error('No se pudo convertir la imagen a WebP');
  return blob;
}

/**
 * Valida, redimensiona, comprime a WebP, aplica la marca de agua DK SUPPLY y
 * sube las variantes optimizadas al almacenamiento público, devolviendo las URLs.
 */
export async function processAndUploadProductImage(file: File): Promise<OptimizedImage> {
  const validationError = validateImageFile(file);
  if (validationError) throw new Error(validationError);

  const realType = await sniffFormat(file);
  if (!realType || !ALLOWED_MIME.includes(realType)) {
    throw new Error('El archivo no es una imagen válida (JPG, PNG o WebP).');
  }

  const bitmap = await loadBitmap(file);
  const sourceWidth = bitmap.width;
  const sourceHeight = bitmap.height;

  if (sourceWidth < MIN_DIMENSION || sourceHeight < MIN_DIMENSION) {
    bitmap.close?.();
    throw new Error(`La imagen es demasiado pequeña (mínimo ${MIN_DIMENSION} px).`);
  }

  const base = `producto_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  let sizes = IMAGE_SIZES.filter((size) => size <= sourceWidth);
  if (sizes.length === 0) sizes = [Math.min(sourceWidth, 400)];

  const variants: ImageVariant[] = [];
  let optimizedBytes = 0;

  try {
    for (const size of sizes) {
      const blob = await renderVariant(bitmap, size, sourceWidth, sourceHeight);
      optimizedBytes += blob.size;
      const path = `${base}-${size}.webp`;
      const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, blob, {
        contentType: 'image/webp',
        cacheControl: '31536000',
        upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      variants.push({ size, url: data.publicUrl });
    }
  } finally {
    bitmap.close?.();
  }

  // Versión principal: 1200 px si existe; si no, la más grande disponible.
  const primary =
    variants.find((v) => v.size === 1200) ?? variants[variants.length - 1];
  if (!primary) throw new Error('No se pudo generar ninguna versión de la imagen');

  return {
    url: primary.url,
    base,
    variants,
    originalBytes: file.size,
    optimizedBytes,
    width: sourceWidth,
    height: sourceHeight,
  };
}

/**
 * Construye un atributo srcset con las variantes disponibles derivadas de la URL.
 * Solo incluye tamaños que existen realmente (según el tamaño de la versión principal).
 */
export function buildImageSrcSet(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  const match = url.match(/^(.*)-(\d+)\.webp$/);
  if (!match) return undefined;
  const prefix = match[1];
  const primarySize = Number(match[2]);
  const available = IMAGE_SIZES.filter((size) => size <= primarySize);
  if (available.length === 0) return undefined;
  return available.map((size) => `${prefix}-${size}.webp ${size}w`).join(', ');
}

/**
 * Devuelve la URL recomendada para un ancho destino aproximado y la URL base.
 * Útil cuando no se usa srcset (por ejemplo, miniaturas del admin).
 */
export function pickImageSize(
  url: string | null | undefined,
  targetWidth: number
): string | null {
  if (!url) return null;
  const match = url.match(/^(.*)-(\d+)\.webp$/);
  if (!match) return url;
  const prefix = match[1];
  const primarySize = Number(match[2]);
  const candidates = IMAGE_SIZES.filter((size) => size <= primarySize);
  const chosen =
    candidates.find((size) => size >= targetWidth) ?? candidates[candidates.length - 1];
  return chosen ? `${prefix}-${chosen}.webp` : url;
}